import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchAllClients,
  fetchFollowingClubs,
  followClient,
} from "../redux/slices/clientFollowSlice";

import {
  getLinkedClubsApi,
} from "../api/ClientActions";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const getUploadUrl = (
  file?: string | null
) => {
  if (!file) {
    return "/assets/images/user.png";
  }

  const value =
    String(file).trim();

  if (!value) {
    return "/assets/images/user.png";
  }

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  if (
    value.startsWith("/uploads/")
  ) {
    return `${API_URL}${value}`;
  }

  if (
    value.startsWith("uploads/")
  ) {
    return `${API_URL}/${value}`;
  }

  if (value.startsWith("/")) {
    return `${API_URL}${value}`;
  }

  return `${API_URL}/uploads/${value}`;
};

const VerifiedTick = () => (
  <span
    className="bs-suggest-verified"
    style={{
      width: "15px",
      height: "15px",
      background: "#2F6FE4",
      borderRadius: "50%",
      verticalAlign: "middle",
      flexShrink: 0,
    }}
  >
    <i
      className="feather-check text-white"
      style={{
        fontSize: "9px",
        fontWeight: 700,
      }}
    />
  </span>
);

const Friends: React.FC = () => {
  const dispatch =
    useDispatch<any>();

  const navigate =
    useNavigate();

  const {
    clients,
    loading,
  } = useSelector(
    (state: any) =>
      state.clientFollow
  );

  const [
    linkedClubs,
    setLinkedClubs,
  ] = useState<any[]>([]);

  const [
    linkedLoading,
    setLinkedLoading,
  ] = useState(false);

  const user = JSON.parse(
    localStorage.getItem("user") ||
      "{}"
  );

  // ==========================
  // LOAD SUGGESTED CLUBS
  // ==========================
  const loadClients = () => {
    if (!user?.user_id) {
      return;
    }

    dispatch(
      fetchAllClients({
        user_id:
          user.user_id,

        follower_user_id:
          user.user_id,
      })
    );
  };

  // ==========================
  // LOAD LINKED CLUBS
  // ==========================
  const loadLinkedClubs =
    async () => {
      if (!user?.user_id) {
        setLinkedClubs([]);
        return;
      }

      try {
        setLinkedLoading(true);

        const result =
          await getLinkedClubsApi(
            user.user_id
          );

        setLinkedClubs(
          Array.isArray(
            result?.data
          )
            ? result.data
            : Array.isArray(
                result?.clients
              )
              ? result.clients
              : []
        );
      } catch (error) {
        console.error(
          "Joined clubs fetch error:",
          error
        );

        setLinkedClubs([]);
      } finally {
        setLinkedLoading(false);
      }
    };

  // ==========================
  // INITIAL LOAD
  // ==========================
  useEffect(() => {
    loadClients();
    loadLinkedClubs();
  }, [
    dispatch,
    user?.user_id,
  ]);

  // ==========================
  // FOLLOW CLUB
  // ==========================
  const handleFollow =
    async (club: any) => {
      if (!user?.user_id) {
        return;
      }

      try {
        const res =
          await dispatch(
            followClient({
              client_id:
                club.client_id,

              follower_user_id:
                user.user_id,

              user_id:
                user.user_id,

              platform:
                "web",
            })
          ).unwrap();

        if (
          res?.status ||
          res?.success
        ) {
          loadClients();

          dispatch(
            fetchFollowingClubs({
              user_id:
                user.user_id,

              follower_user_id:
                user.user_id,
            })
          );
        }
      } catch (error) {
        console.error(
          "Follow club error:",
          error
        );
      }
    };

  // ==========================
  // FILTER SUGGESTED CLUBS
  // ==========================
  const filteredClients =
    clients
      ?.filter(
        (client: any) =>
          Number(
            client.client_status
          ) === 1
      )
      ?.filter(
        (client: any) =>
          !client.is_following
      ) || [];

  return (
    <div className="bs-suggest-card card w-100 border-0 mb-3">
      <div className="bs-suggest-header">
        <div>
          <h4>
            Suggested Clubs
          </h4>

          <p>
            Discover new communities
          </p>
        </div>

        {!loading &&
          filteredClients.length >
            0 && (
            <span className="bs-suggest-count">
              {
                filteredClients.length
              }
            </span>
          )}
      </div>

      {loading && (
        <div className="bs-suggest-skeleton-wrap">
          {Array.from({
            length: 3,
          }).map((_, i) => (
            <div
              className="bs-suggest-skeleton-row"
              key={i}
            >
              <div className="bs-suggest-skeleton-avatar" />

              <div className="bs-suggest-skeleton-text">
                <div className="bs-suggest-skeleton-line bs-suggest-skeleton-line--wide" />

                <div className="bs-suggest-skeleton-line bs-suggest-skeleton-line--narrow" />
              </div>

              <div className="bs-suggest-skeleton-btn" />
            </div>
          ))}
        </div>
      )}

      {!loading &&
        filteredClients.length ===
          0 && (
          <div className="bs-suggest-empty-wrap">
            <div className="bs-suggest-empty">
              <div className="bs-suggest-empty-icon">
                <i className="feather-users" />
              </div>

              <h5>
                No clubs to follow
              </h5>

              <p>
                You are already following
                available clubs or no
                active clubs are
                available right now.
              </p>
            </div>
          </div>
        )}

      {!loading &&
        filteredClients.length >
          0 && (
          <div className="bs-suggest-list bs-suggest-scroll">
            {filteredClients.map(
              (
                client: any,
                index: number
              ) => {
                const clubName =
                  client.client_name ||
                  "Club";

                const clubLevel =
                  client.level
                    ?.level_name ||
                  "Club";

                const memberCount =
                  client.member_count ||
                  0;

                return (
                  <div
                    className="bs-suggest-item"
                    key={
                      client.client_id ||
                      index
                    }
                  >
                    <figure className="bs-suggest-avatar-wrap mb-0">
                      <img
                        src={getUploadUrl(
                          client.logo_image ||
                            client.client_logo
                        )}
                        alt={
                          clubName
                        }
                        onError={(
                          event
                        ) => {
                          event.currentTarget.src =
                            "/assets/images/user.png";
                        }}
                      />
                    </figure>

                    <div
                      className="bs-suggest-info"
                      onClick={() => {
                        if (
                          client.unique_slug
                        ) {
                          navigate(
                            `/club/${client.unique_slug}`
                          );
                        }
                      }}
                    >
                      <div className="bs-suggest-name-row">
                        <h4>
                          {
                            clubName
                          }
                        </h4>

                        {Number(
                          client.verified_status
                        ) === 1 && (
                          <VerifiedTick />
                        )}
                      </div>

                      <span className="bs-suggest-level">
                        {
                          clubLevel
                        }

                        {memberCount
                          ? ` · ${memberCount} Members`
                          : ""}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="bs-suggest-follow-btn"
                      onClick={() =>
                        handleFollow(
                          client
                        )
                      }
                    >
                      Follow
                    </button>
                  </div>
                );
              }
            )}
          </div>
        )}

      <style>{`
        .bs-suggest-card {
          border-radius: 20px;
          background: #ffffff;
          border: 1px solid #DCE4F5 !important;
          box-shadow: 0 8px 24px rgba(10, 35, 84, 0.05);
          overflow: hidden;
        }

        .bs-suggest-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 18px 18px 14px;
        }

        .bs-suggest-header h4 {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color: #0A2354;
          line-height: 1.25;
        }

        .bs-suggest-header p {
          margin: 4px 0 0;
          font-size: 12px;
          font-weight: 500;
          color: #8A93A3;
        }

        .bs-suggest-count {
          min-width: 26px;
          height: 26px;
          padding: 0 9px;
          border-radius: 999px;
          background: rgba(47, 111, 228, 0.12);
          color: #1447B0;
          font-size: 11.5px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .bs-suggest-verified {
          margin-left: 5px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .bs-suggest-skeleton-wrap {
          padding: 2px 14px 14px;
        }

        .bs-suggest-skeleton-row {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #F4F7FC;
          border: 1px solid #DCE4F5;
          border-radius: 16px;
          padding: 11px;
          margin-bottom: 10px;
        }

        .bs-suggest-skeleton-row:last-child {
          margin-bottom: 0;
        }

        .bs-suggest-skeleton-avatar {
          width: 44px;
          height: 44px;
          min-width: 44px;
          border-radius: 50%;
          background: linear-gradient(
            90deg,
            #EAF1FF 25%,
            #F4F7FC 37%,
            #EAF1FF 63%
          );
          background-size: 400% 100%;
          animation: bs-suggest-shimmer 1.4s ease infinite;
        }

        .bs-suggest-skeleton-text {
          flex: 1;
        }

        .bs-suggest-skeleton-line {
          height: 9px;
          border-radius: 5px;
          background: linear-gradient(
            90deg,
            #EAF1FF 25%,
            #F4F7FC 37%,
            #EAF1FF 63%
          );
          background-size: 400% 100%;
          animation: bs-suggest-shimmer 1.4s ease infinite;
        }

        .bs-suggest-skeleton-line--wide {
          width: 70%;
        }

        .bs-suggest-skeleton-line--narrow {
          width: 45%;
          margin-top: 7px;
        }

        .bs-suggest-skeleton-btn {
          width: 76px;
          height: 32px;
          border-radius: 999px;
          flex-shrink: 0;
          background: linear-gradient(
            90deg,
            #EAF1FF 25%,
            #F4F7FC 37%,
            #EAF1FF 63%
          );
          background-size: 400% 100%;
          animation: bs-suggest-shimmer 1.4s ease infinite;
        }

        @keyframes bs-suggest-shimmer {
          0% {
            background-position: 100% 0;
          }

          100% {
            background-position: -100% 0;
          }
        }

        .bs-suggest-empty-wrap {
          padding: 0 18px 22px;
        }

        .bs-suggest-empty {
          background: #F4F7FC;
          border: 1px dashed #DCE4F5;
          border-radius: 16px;
          padding: 22px 16px;
          text-align: center;
        }

        .bs-suggest-empty-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(47, 111, 228, 0.10);
          color: #2F6FE4;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 10px;
        }

        .bs-suggest-empty h5 {
          margin: 0 0 4px;
          font-size: 14px;
          font-weight: 700;
          color: #0A2354;
        }

        .bs-suggest-empty p {
          margin: 0;
          font-size: 12px;
          font-weight: 500;
          color: #8A93A3;
          line-height: 1.5;
        }

        .bs-suggest-list {
          padding: 2px 14px 14px;
        }

        .bs-suggest-scroll {
          max-height: 320px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #CBD5E1 transparent;
        }

        .bs-suggest-scroll::-webkit-scrollbar {
          width: 3px;
        }

        .bs-suggest-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .bs-suggest-scroll::-webkit-scrollbar-thumb {
          background: #CBD5E1;
          border-radius: 999px;
        }

        .bs-suggest-scroll::-webkit-scrollbar-thumb:hover {
          background: #94A3B8;
        }

        .bs-suggest-item {
          background: transparent;
          border: 1px solid #DCE4F5;
          border-radius: 16px;
          padding: 11px;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          transition:
            border-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .bs-suggest-item:last-child {
          margin-bottom: 0;
        }

        .bs-suggest-item:hover {
          border-color: #B9CCF5;
          box-shadow: 0 6px 16px rgba(10, 35, 84, 0.07);
        }

        .bs-suggest-avatar-wrap {
          width: 44px;
          height: 44px;
          min-width: 44px;
          border-radius: 50%;
          padding: 2px;
          background: linear-gradient(
            135deg,
            #EAF1FF,
            #DCE4F5
          );
        }

        .bs-suggest-avatar-wrap img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          display: block;
          background: #EAF1FF;
          border: 2px solid #ffffff;
        }

        .bs-suggest-info {
          min-width: 0;
          flex: 1;
          cursor: pointer;
        }

        .bs-suggest-name-row {
          display: flex;
          align-items: center;
          min-width: 0;
        }

        .bs-suggest-name-row h4 {
          margin: 0;
          font-size: 13px;
          font-weight: 700;
          color: #0A2354;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 145px;
          line-height: 1.3;
        }

        .bs-suggest-level {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: #8A93A3;
          margin-top: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-suggest-follow-btn {
          height: 32px;
          min-width: 76px;
          border: 1px solid #2F6FE4;
          background: #ffffff;
          color: #2F6FE4;
          border-radius: 999px;
          padding: 0 14px;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
          transition: 0.15s ease;
        }

        .bs-suggest-follow-btn:hover {
          background: #2F6FE4;
          color: #ffffff;
          box-shadow: 0 6px 14px rgba(47, 111, 228, 0.28);
        }

        .bs-suggest-follow-btn:active {
          transform: scale(0.97);
        }
      `}</style>
    </div>
  );
};

export default Friends;